import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';
import { AdSizesBoard, PublicationsBoard, PortalOrdersBoard } from '@api/BoardSDK';

let catalogCache: { at: number; value: { sizes: unknown[]; publications: unknown[] } } | null = null;
const CATALOG_TTL = 15 * 60 * 1000;

export const getCatalog = createServerFn({ method: 'GET' })
  .validator(z.object({}).optional())
  .handler(async () => {
    // Rates rarely change — reuse them for 15 minutes so every visitor doesn't use up the daily monday request allowance.
    if (catalogCache && Date.now() - catalogCache.at < CATALOG_TTL) return catalogCache.value as Awaited<ReturnType<typeof loadCatalog>>;
    try {
      const value = await loadCatalog();
      catalogCache = { at: Date.now(), value };
      return value;
    } catch (e) {
      if (catalogCache) { console.error('[catalog] using saved rates after error', e); return catalogCache.value as Awaited<ReturnType<typeof loadCatalog>>; }
      throw e;
    }
  });

const loadCatalog = () => withBoardAuth(async () => {
    const [sizesR, pubsR] = await Promise.allSettled([
      new AdSizesBoard()
        .items()
        .withColumns(['pricePerIssue', 'widthInches', 'heightInches', 'color', 'column3MonthRate', 'column6MonthRate', 'column12MonthRate'])
        .orderBy({ column: 'pricePerIssue', direction: 'desc' })
        .withPagination({ limit: 50 })
        .execute(),
      new PublicationsBoard()
        .items()
        .withColumns(['circulation', 'frequency', 'audience', 'region'])
        .withPagination({ limit: 20 })
        .execute(),
    ]);
    // Publications are optional for the order flow — only the Ad Sizes read is required.
    if (pubsR.status === 'rejected') console.error('[catalog] Publications read failed:', pubsR.reason);
    if (sizesR.status === 'rejected') {
      console.error('[catalog] Ad Sizes read failed:', sizesR.reason);
      throw sizesR.reason; // keep the original error so an expired connection is detected and retried
    }
    return { sizes: sizesR.value.items, publications: pubsR.status === 'fulfilled' ? pubsR.value.items : [] };
  });

const orderSchema = z.object({
  id: z.string().optional(),
  businessName: z.string().min(1),
  contactPerson: z.string().min(1),
  email: z.string().min(3),
  phone: z.string().min(3),
  publication: z.string(),
  adSize: z.string(),
  duration: z.string(),
  startMonth: z.string(),
  monthlyRate: z.number(),
  totalContractValue: z.number(),
  prepaymentDiscount: z.boolean(),
  paymentMethod: z.string(),
  specialInstructions: z.string(),
});

export const createOrder = createServerFn({ method: 'POST' })
  .validator((d: unknown) => orderSchema.parse(d))
  .handler(async ({ data }) => withBoardAuth(async () => {
    const fields = {
        name: data.businessName,
        status: 'New Order' as const,
        orderDate: new Date(),
        contactPerson: data.contactPerson,
        email: { email: data.email, label: data.email },
        phone: { phone: data.phone, country: 'US' },
        publication: data.publication,
        adSize: data.adSize,
        duration: data.duration,
        startMonth: data.startMonth,
        monthlyRate: data.monthlyRate,
        totalContractValue: data.totalContractValue,
        prepaymentDiscount: data.prepaymentDiscount,
        paymentMethod: data.paymentMethod,
        specialInstructions: data.specialInstructions,
    };
    if (data.id) {
      await new PortalOrdersBoard().item(data.id).update(fields).execute();
      return { id: data.id };
    }
    const item = await new PortalOrdersBoard().item().create(fields).execute();
    return { id: item.id };
  }));

export const uploadArtwork = createServerFn({ method: 'POST' })
  .validator((data: FormData) => data)
  .handler(async ({ data }) => withBoardAuth(async () => {
    const file = data.get('file') as File;
    const itemId = data.get('itemId') as string;
    await new PortalOrdersBoard().item(itemId).uploadFile({ columnId: 'file_mm80pqe6' as 'adFiles', file }).execute();
    return { ok: true };
  }));

export const getOrders = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), publication: z.string().optional(), status: z.string().optional(), cursor: z.string().optional() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const board = new PortalOrdersBoard();
    if (data.cursor) {
      const res = await board
        .items()
        .withColumns(['status', 'contactPerson', 'publication', 'adSize', 'duration', 'startMonth', 'totalContractValue', 'paymentMethod', 'adFiles', 'email', 'phone', 'specialInstructions'])
        .withPagination({ cursor: data.cursor })
        .execute();
      return { items: res.items, cursor: res.cursor ?? null };
    }
    const where: Record<string, unknown> = {};
    if (data.publication) where.publication = { contains: data.publication };
    if (data.status) where.status = [data.status];
    const res = await board
      .items()
      .withColumns(['status', 'contactPerson', 'publication', 'adSize', 'duration', 'startMonth', 'totalContractValue', 'paymentMethod', 'adFiles', 'email', 'phone', 'specialInstructions'])
      .where(where as never)
      .withPagination({ limit: 25 })
      .execute();
    return { items: res.items, cursor: res.cursor ?? null };
  }));

export const getStatusCounts = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const rows = await new PortalOrdersBoard().aggregate().groupBy('status').countItems('total').sum('totalContractValue', 'value').execute();
    return rows ?? [];
  }));

/** Staff only: remove orders (archived in monday — restorable from the board's archive for 30 days). */
export const deleteOrders = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), ids: z.array(z.string().min(1)).min(1).max(50) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const board = new PortalOrdersBoard();
    const results = await Promise.allSettled(data.ids.map((id) => board.item(id).archive().execute()));
    const failed = data.ids.filter((_, i) => results[i]?.status === 'rejected');
    const denied = results.some((r) => r.status === 'rejected' && String((r.reason as { message?: string })?.message ?? r.reason).includes('USER_UNAUTHORIZED'));
    results.forEach((r) => { if (r.status === 'rejected') console.error('[orders] delete failed', r.reason); });
    return { failed, denied };
  }));
